import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import Modal, { ModalTitle, ModalSubtitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import ScheduleStep from '../../components/ScheduleStep/ScheduleStep';
import { useToast } from '../../components/Toast/ToastContext';
import { combineDateTime, formatScheduledFor, scheduleValidationError, splitDateTime } from '../../lib/scheduling';
import { mapScheduledHandoff } from '../../lib/deviceAdapters';
import {
  rescueCancelScheduledHandoffMutation,
  rescueRescheduleHandoffMutation,
  rescueScheduledHandoffsQuery,
  type RescueScheduledHandoffsData,
} from '../../lib/rescueQueries';
import type { ScheduledHandoff, ScheduledHandoffKind } from '../../lib/types';
import styles from './ScheduledHandoffsModal.module.scss';

interface ScheduledHandoffsModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Open straight onto this booking's reschedule step instead of the list. Set when the rescue got
   * here from a pet's "Edit scheduled ..." action, where they already know which one they meant.
   */
  focusHandoffId?: string | null;
  /** Which step to land on for `focusHandoffId`. Defaults to changing its date and time. */
  focusAction?: 'reschedule' | 'cancel';
}

const KIND_LABELS: Record<ScheduledHandoffKind, string> = {
  ADOPTION: 'Adoption',
  FOSTER: 'Foster placement',
  ADOPTION_FROM_FOSTER: 'Adoption (Foster Fail)',
};

/**
 * Everything this rescue has booked for later, with the two things a booking needs after the fact:
 * move the time, or call it off. A modal rather than a tab — it is a short list a rescue checks
 * occasionally, not a place they work.
 */
export default function ScheduledHandoffsModal({
  open,
  onClose,
  focusHandoffId,
  focusAction = 'reschedule',
}: ScheduledHandoffsModalProps) {
  const { showToast } = useToast();
  const [rescheduling, setRescheduling] = useState<ScheduledHandoff | null>(null);
  const [cancelling, setCancelling] = useState<ScheduledHandoff | null>(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery<RescueScheduledHandoffsData>(rescueScheduledHandoffsQuery, {
    skip: !open,
    fetchPolicy: 'cache-and-network',
  });

  const [rescheduleMut] = useMutation(rescueRescheduleHandoffMutation);
  const [cancelMut] = useMutation(rescueCancelScheduledHandoffMutation);

  const handoffs = (data?.rescueScheduledHandoffs ?? [])
    .map((h) => mapScheduledHandoff(h))
    .filter((h): h is ScheduledHandoff => !!h);

  // Deferred to an effect rather than derived: the booking is only knowable once the query resolves,
  // and the rescue must stay free to hit Back afterwards without being bounced straight here again.
  const [focusApplied, setFocusApplied] = useState(false);
  useEffect(() => {
    if (!open) {
      setFocusApplied(false);
      return;
    }
    if (focusApplied || !focusHandoffId) {
      return;
    }
    const match = handoffs.find((h) => h.id === focusHandoffId);
    if (match) {
      setFocusApplied(true);
      if (focusAction === 'cancel') {
        setError(null);
        setCancelling(match);
      } else {
        beginReschedule(match);
      }
    }
  }, [open, focusApplied, focusHandoffId, focusAction, handoffs]);

  /** Opens the reschedule step with the fields already holding the booking's current date and time. */
  function beginReschedule(handoff: ScheduledHandoff) {
    const current = splitDateTime(handoff.scheduledFor, handoff.scheduledForTimezone);
    setDate(current.date);
    setTime(current.time);
    setError(null);
    setRescheduling(handoff);
  }

  function resetSubSteps() {
    setRescheduling(null);
    setCancelling(null);
    setDate('');
    setTime('');
    setError(null);
  }

  function handleClose() {
    resetSubSteps();
    onClose();
  }

  const scheduleError = scheduleValidationError(date, time);
  const when = combineDateTime(date, time);

  async function confirmReschedule() {
    if (!rescheduling || !when || scheduleError || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await rescheduleMut({ variables: { input: { id: rescheduling.id, ...when } } });
      await refetch();
      showToast('Rescheduled');
      resetSubSteps();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reschedule.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmCancel() {
    if (!cancelling || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await cancelMut({ variables: { input: { id: cancelling.id } } });
      await refetch();
      showToast('Scheduled transfer cancelled');
      resetSubSteps();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not cancel.');
    } finally {
      setBusy(false);
    }
  }

  if (rescheduling) {
    return (
      <Modal open={open} onClose={handleClose} dismissOnBackdropClick={false}>
        <ModalTitle>Change Date/Time</ModalTitle>
        <ScheduleStep
          actionLabel={KIND_LABELS[rescheduling.kind]}
          date={date}
          time={time}
          onDateChange={setDate}
          onTimeChange={setTime}
          error={scheduleError ?? error}
          compact
        >
          <ModalSubtitle>
            {KIND_LABELS[rescheduling.kind]} for <strong>{rescheduling.petName ?? 'this pet'}</strong>
          </ModalSubtitle>
        </ScheduleStep>
        <ModalActions>
          <Button variant="ghost" onClick={resetSubSteps} disabled={busy}>
            Back
          </Button>
          <Button variant="green" onClick={confirmReschedule} disabled={!when || !!scheduleError || busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </ModalActions>
      </Modal>
    );
  }

  if (cancelling) {
    return (
      <Modal open={open} onClose={handleClose} dismissOnBackdropClick={false}>
        <ModalTitle>Cancel this scheduled {KIND_LABELS[cancelling.kind].toLowerCase()}?</ModalTitle>
        <ModalSubtitle>
          The {KIND_LABELS[cancelling.kind].toLowerCase()} for <strong>{cancelling.petName ?? 'this pet'}</strong> on{' '}
          {formatScheduledFor(cancelling.scheduledFor, cancelling.scheduledForTimezone)} won&apos;t run. Nothing has
          happened yet, so you can schedule it again any time.
        </ModalSubtitle>
        {error && <div className={styles.error}>{error}</div>}
        <ModalActions>
          <Button variant="ghost" onClick={resetSubSteps} disabled={busy}>
            Keep it
          </Button>
          <Button variant="danger" onClick={confirmCancel} disabled={busy}>
            {busy ? 'Cancelling…' : 'Yes, cancel it'}
          </Button>
        </ModalActions>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={handleClose} size="wide">
      <ModalTitle>Scheduled transfers and adoptions</ModalTitle>
      <ModalSubtitle>Nothing changes for a pet or its collar until its scheduled time.</ModalSubtitle>

      {loading && handoffs.length === 0 && <div className={styles.empty}>Loading…</div>}

      {!loading && handoffs.length === 0 && <div className={styles.empty}>Nothing scheduled yet.</div>}

      {handoffs.length > 0 && (
        <ul className={styles.list}>
          {handoffs.map((handoff) => (
            <li key={handoff.id} className={styles.row}>
              <div className={styles.rowMain}>
                <div className={styles.rowTitle}>
                  <strong>{handoff.petName ?? 'Pet'}</strong>
                  <StatusBadge variant="info">{KIND_LABELS[handoff.kind]}</StatusBadge>
                </div>
                <div className={styles.rowMeta}>
                  {formatScheduledFor(handoff.scheduledFor, handoff.scheduledForTimezone)}
                  {handoff.recipientName ? ` · ${handoff.recipientName}` : ''}
                </div>
                {handoff.failureReason && <div className={styles.rowError}>{handoff.failureReason}</div>}
              </div>
              <div className={styles.rowActions}>
                <Button variant="ghost" size="sm" onClick={() => beginReschedule(handoff)}>
                  Change date/time
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setCancelling(handoff);
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ModalActions>
        <Button variant="ghost" onClick={handleClose}>
          Done
        </Button>
      </ModalActions>
    </Modal>
  );
}
